/* Search (#2555), to Joni's canvas: a 640 px dialog with a fixed results height; full screen on a phone */

.container {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-block-size: 0;
  color: var(--tuna-content-primary);
}

.visuallyHidden {
  position: absolute;
  inline-size: 1px;
  block-size: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

.header {
  display: flex;
  align-items: center;
  gap: var(--tuna-spacing-1);
  padding: var(--tuna-spacing-2) var(--tuna-spacing-2) 12px;
}

.field {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 10px;
  min-inline-size: 0;
  min-block-size: 48px;
  padding: 0 12px;
  border: 2px solid var(--tuna-palette-primary-main);
  border-radius: var(--tuna-shape-radius-md);
}

.fieldIcon {
  flex: none;
  inline-size: 18px;
  block-size: 18px;
  color: var(--tuna-content-secondary);
}

/* 16 px, so a phone does not zoom into the field */
.input {
  flex: 1;
  min-inline-size: 0;
  padding: 0;
  font: inherit;
  font-size: var(--tuna-typography-body1-fontSize);
  color: var(--tuna-content-primary);
  background: transparent;
  border: 0;
  outline: none;
}

.clear {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  inline-size: 32px;
  block-size: 32px;
  padding: 0;
  color: var(--tuna-content-secondary);
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: var(--tuna-shape-radius-sm);
}

.clear svg {
  inline-size: 16px;
  block-size: 16px;
}

.cancel {
  flex: none;
  min-block-size: 44px;
  padding: 0 var(--tuna-spacing-1);
  font: inherit;
  font-size: var(--tuna-typography-body1-fontSize);
  font-weight: 600;
  color: var(--tuna-palette-primary-main);
  cursor: pointer;
  background: transparent;
  border: 0;
}

.controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 0 var(--tuna-spacing-2) 12px;
}

.controls:empty {
  display: none;
}

/* "In {space} | Everywhere" */
.scope {
  display: flex;
  flex: none;
  overflow: hidden;
  border: 1px solid var(--tuna-border-default);
  border-radius: var(--tuna-shape-radius-md);
}

.scopeOption {
  min-block-size: 32px;
  padding: 0 12px;
  font: inherit;
  font-size: var(--tuna-typography-body2-fontSize);
  color: var(--tuna-content-primary);
  cursor: pointer;
  background: var(--tuna-surface-default);
  border: 0;
}

.scopeOption + .scopeOption {
  border-inline-start: 1px solid var(--tuna-border-default);
}

.scopeChecked {
  font-weight: 600;
  background: color-mix(in srgb, var(--tuna-palette-selection) 20%, transparent);
}

.scopeOption:focus-visible {
  outline: 2px solid var(--tuna-palette-primary-main);
  outline-offset: -2px;
}

.results {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-block-size: 0;
  padding: var(--tuna-spacing-1);
  overflow-y: auto;
  border-block-start: 1px solid var(--tuna-border-subtle);
}

/* Search's dialog: a fixed height, so the dialog does not jump as results arrive */
.resultsFixed {
  flex: none;
  block-size: 420px;
}

.listbox {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.option {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  min-block-size: 52px;
  padding: 4px 12px;
  cursor: pointer;
  border-radius: var(--tuna-shape-radius-md);
}

.option:hover,
.optionActive {
  background: color-mix(in srgb, var(--tuna-palette-selection) 20%, transparent);
}

.optionAction {
  flex: none;
  margin-inline-start: auto;
}

/* The scoped list's last line: "{n} more in other spaces · Search everywhere" */
.moreElsewhere {
  display: flex;
  flex-wrap: wrap;
  gap: var(--tuna-spacing-1);
  align-items: center;
  /* Right after the last result: pinned to the bottom, it would sit behind a phone's keyboard (Joni, #2562) */
  margin: 0;
  padding: 12px;
  font-size: var(--tuna-typography-body2-fontSize);
  color: var(--tuna-content-secondary);
  border-block-start: 1px solid var(--tuna-border-subtle);
}

.link {
  padding: 0;
  font: inherit;
  font-weight: 600;
  color: var(--tuna-palette-primary-main);
  cursor: pointer;
  background: none;
  border: 0;
}

.link:hover {
  text-decoration: underline;
}

/* 44 px targets on touch (Joni, #2562) */
@media (pointer: coarse) {
  .clear {
    inline-size: 44px;
    block-size: 44px;
  }

  .scopeOption {
    min-block-size: 44px;
  }
}

/* Phone (screen F): the results take the space above the keyboard, and 44 px targets */
@media (max-width: 599.95px) {
  .header {
    padding: 12px var(--tuna-spacing-1) var(--tuna-spacing-1) var(--tuna-spacing-2);
  }

  .field {
    min-block-size: 44px;
  }

  .controls {
    flex-wrap: nowrap;
    overflow-x: auto;
  }

  .resultsFixed {
    flex: 1;
    block-size: auto;
  }

  .option {
    min-block-size: 56px;
  }
}

/* PR 2 (#2555 M4–M6) */

/* A thin bar while searching; the slot keeps its height so nothing jumps */
.progress {
  block-size: 4px;
}

.error {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px var(--tuna-spacing-2);
  font-size: var(--tuna-typography-body2-fontSize);
  color: var(--tuna-content-primary);
  background: color-mix(in srgb, var(--tuna-feedback-error) 12%, transparent);
}

.error span {
  flex: 1;
}

/* The earlier results under an error: faded, and hidden from assistive tech by the markup */
.dimmed .listbox {
  opacity: 0.55;
}

.heading {
  margin: 0;
  padding: var(--tuna-spacing-1) 12px 4px;
  font-size: var(--tuna-typography-caption-fontSize);
  font-weight: 600;
  color: var(--tuna-content-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* No match, and a new account (screens C, D, E): a line, then its action */
.emptyState {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: auto;
  padding: var(--tuna-spacing-3);
  text-align: center;
}

.emptyState p {
  margin: 0;
}

.emptyHint {
  font-size: var(--tuna-typography-body2-fontSize);
  color: var(--tuna-content-secondary);
}
